import React from 'react';
import { useTranslation } from 'react-i18next';
import { googleLinkTacke, otvoriGoogle } from './googleLinkovi';

/* Bocni panel sa detaljima izabranog vozila.

   Podaci stizu iz dva izvora i to se namerno vidi u rasporedu:
     - gornji deo: nasa baza (pozicija, karton iz voznog parka) — uvek stigne
     - donji deo: Mobilisis (gorivo, motorsati, danasnja statistika) — dodatak

   Ako Mobilisis zakaze, panel se svejedno prikazuje, samo bez donjeg dela i uz
   tihu napomenu. */

// Sekunde -> "2h 15min". Trajanja iz Mobilisis-a dolaze u sekundama.
const trajanje = (s) => {
    if (s == null) return null;
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return h ? `${h}h ${m}min` : `${m}min`;
};

const broj = (v, decimala = 0) =>
    v == null ? null : Number(v).toLocaleString('sr-RS', {
        minimumFractionDigits: decimala, maximumFractionDigits: decimala
    });

// Red se ne prikazuje ako nema vrednost — prazan red samo troši prostor.
const Red = ({ naziv, vrednost, naglaseno }) => {
    if (vrednost === null || vrednost === undefined || vrednost === '') return null;
    return (
        <div className={`gps-panel-red${naglaseno ? ' gps-panel-red-jak' : ''}`}>
            <span className="gps-panel-naziv">{naziv}</span>
            <span className="gps-panel-vrednost">{vrednost}</span>
        </div>
    );
};

/* Boja stavke prati istu skalu kao spisak alarma na mapi: isteklo crveno,
   nedelja dana narandzasto, dalje neutralno. */
const bojaAlarma = (dana) => {
    if (dana < 0) return 'gps-alarm-isteklo';
    if (dana <= 7) return 'gps-alarm-uskoro';
    return '';
};

const PanelVozila = ({ detalji, ucitava, onZatvori, onPlanRute, stanje, alarmi = [] }) => {
    const { t } = useTranslation();

    const v = detalji?.vozilo;
    const m = detalji?.mobilisis;
    const d = detalji?.danas;

    const vrstaTekst = v?.vrsta === 'POLUPRIKOLICA' ? t('gps.poluprikolica')
                     : v?.vrsta === 'PRIKOLICA'     ? t('gps.prikolica')
                     : v?.vrsta_naziv || null;

    return (
        <aside className="gps-panel">
            <div className="gps-panel-vrh">
                <div>
                    <div className="gps-panel-reg">{v?.registracija || '—'}</div>
                    {vrstaTekst && <div className="gps-panel-vrsta">{vrstaTekst}</div>}
                </div>
                {/* Gasi pracenje ovog vozila i vraca prikaz cele flote. */}
                <button
                    type="button"
                    className="gps-panel-x"
                    onClick={onZatvori}
                    aria-label={t('gps.prikaziSve')}
                    title={t('gps.prikaziSve')}
                >
                    <i className="pi pi-times" />
                </button>
            </div>

            {ucitava && !detalji && (
                <div className="gps-panel-cekanje">
                    <i className="pi pi-spin pi-spinner" /> {t('gps.ucitavanje')}
                </div>
            )}

            {v && (
                <>
                    <div className={`gps-panel-stanje gps-panel-${stanje}`}>
                        {stanje === 'vozi' ? t('gps.vozi') : stanje === 'stoji' ? t('gps.stoji') : t('gps.bezSignala')}
                        {v.brzina != null && <span className="gps-panel-brzina">{v.brzina} km/h</span>}
                    </div>

                    {/* Alarmi baš ovog vozila stoje PRE položaja — istekao dokument je prvo
                        što treba da se vidi kad se vozilo otvori, a ne nešto do čega se
                        dolazi listanjem. Kad ih nema, sekcije nema. Klika nema, za
                        razliku od spiska na mapi: vozilo je već izabrano. */}
                    {alarmi.length > 0 && (
                        <section className="gps-panel-grupa">
                            <h4>{t('gps.alarmiVozila')}</h4>
                            {alarmi.map((a, i) => (
                                <div key={`${a.dokument}-${i}`}
                                     className={`gps-panel-alarm ${bojaAlarma(a.dana_do)}`}>
                                    <span className="gps-panel-alarm-dok">{a.dokument}</span>
                                    <span className="gps-panel-alarm-datum">
                                        {a.datum_od && (
                                            <>
                                                <span className="gps-alarm-dan">{a.datum_od}</span>{' – '}
                                            </>
                                        )}
                                        <span className="gps-alarm-dan">{a.datum}</span>
                                        <em>
                                            {a.dana_do < 0
                                                ? t('gps.istekloPre', { dana: Math.abs(a.dana_do) })
                                                : t('gps.istekZa', { dana: a.dana_do })}
                                        </em>
                                    </span>
                                    {/* Ista beleska kao u spisku na mapi — da se ne
                                        trazi na dva mesta razlicito. */}
                                    {a.napomena && (
                                        <span className="gps-alarm-napomena" title={a.napomena}>
                                            <i className="pi pi-comment" />
                                            <span>{a.napomena}</span>
                                        </span>
                                    )}
                                </div>
                            ))}
                        </section>
                    )}

                    {/* Planiranje rute stoji PRE podataka o polozaju: podaci se
                        citaju, a ovo je radnja — i jedini ulaz koji se primeti,
                        jer dvoklik na vozilo niko ne pogodi slucajno. */}
                    {!!onPlanRute && (
                        <button type="button" className="gps-panel-plan" onClick={onPlanRute}>
                            <i className="pi pi-directions" />
                            {t('gps.planNaslov')}
                        </button>
                    )}

                    <section className="gps-panel-grupa">
                        <h4>{t('gps.polozaj')}</h4>
                        <Red naziv={t('gps.poslednjeJavljanje')} vrednost={v.vreme} />
                        <Red naziv={t('gps.starost')}
                             vrednost={v.minuta_staro != null ? trajanje(v.minuta_staro * 60) : null} />
                        <Red naziv={t('gps.kilometraza')} vrednost={broj(v.km)} />
                        <Red naziv={t('gps.koordinate')}
                             vrednost={v.lat != null ? (
                                 /* Koordinate ostaju OBICAN TEKST, a otvaranje u Google-u
                                    je malo dugme pored. Kao <a> su nasledile Bootstrap-ov
                                    globalni stil (plavo, podvuceno, veci font) i red je
                                    vikao jace od svih ostalih u panelu — a nije vazniji.
                                    Isto resenje kao na kartici stajanja. */
                                 <span className="gps-panel-koord">
                                     {`${v.lat.toFixed(5)}, ${v.lng.toFixed(5)}`}
                                     <button
                                         type="button"
                                         className="gps-panel-koord-dugme"
                                         title={t('gps.pinOtvoriUGoogle')}
                                         aria-label={t('gps.pinOtvoriUGoogle')}
                                         onClick={() => otvoriGoogle(googleLinkTacke(v))}
                                     >
                                         <i className="pi pi-external-link" />
                                     </button>
                                 </span>
                             ) : null} />
                        <Red naziv={t('gps.tacakaZa24h')} vrednost={broj(v.tacaka_24h)} />
                    </section>

                    <section className="gps-panel-grupa">
                        <h4>{t('gps.vozilo')}</h4>
                        <Red naziv={t('gps.marka')}
                             vrednost={[v.marka, v.model].filter(Boolean).join(' ').trim() || m?.naziv || null} />
                        <Red naziv={t('gps.godiste')}
                             vrednost={v.godina ? String(v.godina).slice(0, 4) : null} />
                        <Red naziv={t('gps.sasija')} vrednost={v.Sasija} />
                        <Red naziv={t('gps.nadgradnja')} vrednost={v.nadgradnja} />
                        <Red naziv={t('gps.gorivoVrsta')} vrednost={v.gorivo_vrsta} />
                        <Red naziv={t('gps.rezervoar')}
                             vrednost={v.KapacitetRezervoara ? `${broj(v.KapacitetRezervoara)} l` : null} />
                        {/* Isti redosled kao u oblacicu: kartica, pa nalog. `m?.vozac`
                            stize iz Mobilisis /v1/vehicles i takodje je kartica — ostaje
                            kao rezerva ako snimak pozicija jos nije stigao. */}
                        <Red naziv={t('gps.vozac')}
                             vrednost={v.vozac || m?.vozac
                                 || (v.vozac_nalog ? `${v.vozac_nalog} (${t('gps.poNalogu')})` : null)} />
                        {/* Kad nalog nije u toku, par je poslednji poznat — to mora
                            da se vidi, inace izgleda kao da je prikolica jos zakacena. */}
                        <Red naziv={t('gps.par')}
                             vrednost={v.reg_vucno
                                 ? `${v.reg_vucno}${v.reg_prikljucno ? ' / ' + v.reg_prikljucno : ''}`
                                   + (v.nalog_aktivan ? '' : ` (${t('gps.poslednjiNalog')})`)
                                 : null} />
                        <Red naziv={t('gps.sektor')} vrednost={m?.sektor} />
                        <Red naziv={t('gps.garazniBroj')} vrednost={m?.garazni_broj} />
                    </section>

                    {m && (
                        <section className="gps-panel-grupa">
                            <h4>{t('gps.telemetrija')}</h4>
                            <Red naziv={t('gps.gorivoURezervoaru')}
                                 vrednost={m.gorivo_l != null ? `${broj(m.gorivo_l, 1)} l` : null} naglaseno />
                            <Red naziv={t('gps.potrosnjaNedelja')}
                                 vrednost={m.potrosnja_nedelja != null ? `${broj(m.potrosnja_nedelja, 1)} l/100km` : null} />
                            <Red naziv={t('gps.motorsati')}
                                 vrednost={m.motorsati != null ? `${broj(m.motorsati)} h` : null} />
                        </section>
                    )}

                    {d && (
                        <section className="gps-panel-grupa">
                            <h4>{t('gps.danas')}</h4>
                            <Red naziv={t('gps.predjeno')}
                                 vrednost={d.distance != null ? `${broj(d.distance, 1)} km` : null} naglaseno />
                            <Red naziv={t('gps.vremeVoznje')} vrednost={trajanje(d.timeDriving)} />
                            {/* Ler je direktan trosak goriva — zato stoji uz predjeno, ne zakopan. */}
                            <Red naziv={t('gps.ler')} vrednost={trajanje(d.engineTimeIdling)} naglaseno />
                            <Red naziv={t('gps.zaustavljanja')} vrednost={broj(d.stopsCount)} />
                            <Red naziv={t('gps.prvaVoznja')}
                                 vrednost={d.firstMove ? d.firstMove.slice(11, 16) : null} />
                            <Red naziv={t('gps.poslednjaVoznja')}
                                 vrednost={d.lastMove ? d.lastMove.slice(11, 16) : null} />
                            {d.distanceOverLimit > 0 && (
                                <Red naziv={t('gps.prekoracenje')}
                                     vrednost={`${broj(d.distanceOverLimit, 1)} km / ${trajanje(d.timeOverLimit)}`} />
                            )}
                            <Red naziv={t('gps.potrosnjaDanas')}
                                 vrednost={d.fuel100km ? `${broj(d.fuel100km, 1)} l/100km` : null} />
                        </section>
                    )}

                    {detalji?.greska_mobilisis && (
                        <div className="gps-panel-napomena">{t('gps.bezMobilisisa')}</div>
                    )}
                </>
            )}
        </aside>
    );
};

export default PanelVozila;
